/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-callback-return */
import React, { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import { debounce, formatDateByTimezone } from '../../utils/commonFunctions.js';
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { APIEndpoint } from "../../utils/axios";
import closeIcon from "../../images/icons/Icon2.png";
import { ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

const Header = forwardRef(({ headerProp }, ref) => {
  const {
    inputFieldFocus,
    checkedItemsList,
    filters,
    setScanHistoryArray,
    setScannerData,
    isLoading,
    itemMasterAll,
    setItemMasterAll,
    filterItemsType,
    setMessage,
  } = headerProp;

  const debouncedFetchRef = useRef();
  const initialLoading = useRef(false);
  const inputRef = useRef(null);
  const barcodeItemCacheRef = useRef(new Map());

  const [state, setState] = useState({ startDate: null, isOpenMenu: false, catGroup: [] });
  const [sectors, setSectors] = useState([]);
  const navigate = useNavigate();

useEffect(() => {
  if (inputRef.current) {
    const el = inputRef.current;
    el.value = ""; 
    el.focus();

    const handleBlur = () => {
      setTimeout(() => {
        if (inputRef.current) {   
          inputRef.current.focus();
        }
      }, 0);
    };

    el.addEventListener('blur', handleBlur);

    return () => {
      if (el) {
        el.removeEventListener('blur', handleBlur);
      }
    };
  }
}, [filterItemsType, state.startDate, itemMasterAll, sectors, inputFieldFocus, checkedItemsList]);


  useEffect(() => {
    if (filters) {
      if (!itemMasterAll.length && !sectors.length && !initialLoading.current) {
        fetchScannerItemsAndFilters();
      }
      setState((_) => ({ ..._, startDate: filters.targetDate }));
    }
  }, [filters]);

useEffect(() => {
  barcodeItemCacheRef.current.clear();
}, [filterItemsType]);

  const itemTypeScanned = () => {
    for (let i in filterItemsType) {
      if (Boolean(filterItemsType[i].checked)) {
        return i.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (char) => char.toUpperCase());
      }
    }
  };

  const onChangeBarCode = (e) => {
    const raw = e.target.value.trim();
    // eslint-disable-next-line no-control-regex
    const searchCode = raw.replace(/[\x00-\x1F\x7F-\x9F\u200B-\u200D\uFEFF]/g, '');
    if (searchCode && debouncedFetchRef.current) {
      debouncedFetchRef.current(searchCode);
    }
  };

  const updateScanHistory = (status, sId, itemType) => {
    setScanHistoryArray((prev) => {
      const newHistory = (prev ?? []).map((entry) =>
        entry.scanId === sId && entry.itemType === itemType
          ? { ...entry, status, itemType }
          : entry
      );
      return newHistory;
    });
  };

  const passesFilterCheck = (item) => {
    const { dutyFree, itemId } = item;

    if (!filterItemsType.all.checked) {
      if (
        (dutyFree && !filterItemsType.dutyFree.checked) ||
        (!dutyFree && !filterItemsType.dutyPaid.checked)
      ) {
        return false;
      }
    }

    if (filters && filters.items?.length) {
      const availableItems = new Set(filters.items.map((i) => i.itemId));
      if (!availableItems.has(itemId)) return false;
    }

    return true;
  };

  const fetchBarCodeOnId = useCallback(
    async (searchCode) => {

      setScanHistoryArray((prev) => [
        ...(prev ?? []),
        {
          key: searchCode,
          scanId: searchCode,
          scanTime: moment().format("DD-MM-YY hh:mm A"),
          itemType: itemTypeScanned(),
          status: "Scanning...",
        },
      ]);

      const cachedItem = barcodeItemCacheRef.current.get(searchCode);
 
      if (cachedItem) {
        if (cachedItem.itemName === "NOT_FOUND") {
          updateScanHistory("Not Found", searchCode, itemTypeScanned());
          setMessage({ error: `No Item available with Barcode: ${searchCode}`, duration: 1500 });
          return;
        }

        if (!passesFilterCheck(cachedItem)) {
          updateScanHistory("Not Found", searchCode, itemTypeScanned());
          setMessage({ error: `No Item available with Barcode: ${searchCode}`, duration: 1500 });
          return;
        }

        setScannerData((prev) => {
          const updated = updateScannerData(prev, cachedItem);
          setMessage({ text: `${cachedItem.itemName} count incremented by 1`, duration: 1500 });
          return updated;
        });
        updateScanHistory("Success", searchCode, itemTypeScanned());
        return;
      }

      const barCodeItem = itemMasterAll.find((item) => item.barcode === searchCode);

      if (!barCodeItem) {
        barcodeItemCacheRef.current.set(searchCode, { itemName: "NOT_FOUND" });
        updateScanHistory("Not Found", searchCode, itemTypeScanned());
        setMessage({ error: `No Item available with Barcode: ${searchCode}`, duration: 1500 });
        return;
      }

      if (!passesFilterCheck(barCodeItem)) {
        barcodeItemCacheRef.current.set(searchCode, { itemName: "NOT_FOUND" });
        updateScanHistory("Not Found", searchCode, itemTypeScanned());
        setMessage({ error: `No Item available with Barcode: ${searchCode}`, duration: 1500 });
        return;
      }

      barcodeItemCacheRef.current.set(searchCode, {
        itemId: barCodeItem.itemId,
        itemName: barCodeItem.itemName,
        dutyFree: barCodeItem.dutyFree,
      });

      setScannerData((prev) => {
        const updated = updateScannerData(prev, barCodeItem);
        setMessage({ text: `Found Item: ${barCodeItem.itemName}`, duration: 1500 });
        return updated;
      });

      updateScanHistory("Success", searchCode, itemTypeScanned());
  
    },
    [itemMasterAll, filters, filterItemsType]
  );

  const updateScannerData = (prev, barCodeItem) => {
    const existingItems = prev?.items ?? [];
    const matchedItems = filters?.items?.filter((item) => item?.itemId === barCodeItem.itemId) ?? [];

    matchedItems.forEach((item) => {
      item.totalUplifted = (item.totalUplifted ?? 0) + 1;
    });

    const totScannedObj = [...existingItems, ...matchedItems];
    const newItemIndex = totScannedObj.findIndex((item) => item.itemId === barCodeItem.itemId);
    if (newItemIndex !== -1) {
      const [updatedScan] = totScannedObj.splice(newItemIndex, 1);
      totScannedObj.unshift(updatedScan);
    }

    const uniqueItems = Array.from(
      new Map(totScannedObj.map((item) => [item.itemId, item])).values()
    );

    return { ...prev, items: uniqueItems };
  };

  useEffect(() => {
    debouncedFetchRef.current = debounce((code) => {
      fetchBarCodeOnId(code);
    }, 100);
  }, [fetchBarCodeOnId, itemMasterAll]);

  const fetchItemMasterAll = async (page = 1, perPage = 50) => {
    const filterData = {
      action: 4,
      collection: '',
      filterOperation: 'or',
      filters: [],
      limit: perPage,
      page: page,
      searchParam: ''
    };

    try {
      const res = await APIEndpoint.post('master/process', filterData);
      if (res) return res;
    } catch (err) {
      console.error(err);
    }
  };

  async function getItemMasterAll() {
    try {
      const initialRes = await fetchItemMasterAll(1, 1);
      if (initialRes?.count && Array.isArray(initialRes.items) && initialRes.items.length) {
        const fullRes = await fetchItemMasterAll(1, initialRes.count);
        setItemMasterAll([...fullRes.items]);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  async function fetchScannerItemsAndFilters() {
    try {
      initialLoading.current = true;
      const [sectors, itemsAll] = await Promise.all([
        APIEndpoint.get('stores'),
        getItemMasterAll(),
      ]);

      if (sectors?.success && Array.isArray(sectors.result) && itemsAll) {
        const sectorList = sectors.result.map((data) => ({
          key: data.id,
          label: `${data.flightNumber}-${data.origin}-${data.destination}`,
          value: data.id,
        }));
        if (sectorList.length > 0) setSectors(sectorList);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  }

  const goBack = () => navigate(-1);

  const getScanScreenMode = () => {
    if (!filters) return "";
    if (filters.reconId && !filters.upliftEstimateId) return "Edit Retrieval - ";
    if (!filters.reconId && !filters.upliftEstimateId) return "New Retrieval - ";
    return "Uplifts - ";
  };

  return (
    <div className="header" style={{ position: "relative", zIndex: 1000, top: 0 }}>
      <div className="flexRow justifyContentSpaceBetween alignItemsCenter content-tabs filters">
        {state.startDate ? (
          <>
            <div className="se-scanner-la3">
              {getScanScreenMode()}
              {moment(formatDateByTimezone(state.startDate ? new Date(state.startDate) : new Date())).format("DD-MM-YYYY")} -{" "}
              {filters?.warehouseName} -{" "}
              {filters?.storeName
                ? sectors.find((option) => option.label === filters?.storeName?.replaceAll(" ", ""))?.label
                : sectors.find((option) => option.value === filters?.storeId)?.label || null}
            </div>

            <div className="flexRow justifyContentFlexEnd margTop11">
              <button onClick={goBack}>
                <img src={closeIcon} alt="close" data-icon="close" />
              </button>
            </div>
          </>
        ) : null}
      </div>

      <input
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }}
        ref={inputRef}
        className="barcode-input"
        disabled={isLoading || (!itemMasterAll.length && !sectors.length)}
        type="text"
        name="barcode"
        onChange={onChangeBarCode}
        placeholder="Please enter a barcode or enable scanning to list items..."
      />

      <ToastContainer />
    </div>
  );
});

export default Header;

